import { DetailDescriptionDataSource } from '../DetailDescriptionDataSource';
import { EpgUtil } from '../../utility/EpgUtil';
import { AssetType, StreamType } from '../../../api/models/Shared.types';
import { Utils } from '@lightningjs/sdk';
import { getDayInfoString, getTime, timeStringToMs } from '../../utility/DateFormatter';
import { DetailTranslationKeys, RecordingTranslationKeys } from '../../../api/models/TranslationKeys';
import { GlobalSettingsKeys } from '../../../api/models/GlobalSettingsKeys';
import { Backend } from '../../../api/Backend';
import { ChannelModel } from '../../../api/EpgApi.types';
import { IProgramV5, IScheduleV5, ISeasonV5 } from '../../../api/models/AndromeTV.types';
import { Recording } from '../../../api/user/RecordingApi.types';
import { IconDef, IDetailDescription } from '../detail.types';
import { getEpgWatchability } from './EpgWatchability';
import { lngSupportsAdvancedTextRenderer } from '../../utility/Utility';
import { AccessibilityIcons, AccessibilityTypes } from '../../AccessibilityTypes';
import { getAgeratingIcon } from '../DetailDSUtil';

interface GeneralInfo {
  title: string;
  synopsis: string;
  accessibilityIcons: IconDef[] | undefined;
  recordingIcons: IconDef[] | undefined;
  ageRating: IconDef | undefined;
  credits: Map<string, string> | undefined;
  bookmarkPercentage: number | undefined;
  blackout: string | undefined;
}

export class EpgDetailDescriptionDataSource extends DetailDescriptionDataSource {
  private program: IProgramV5 | undefined;
  private channel: ChannelModel | undefined;
  private season: ISeasonV5 | undefined;
  private schedule: IScheduleV5 | undefined;
  private recording: Recording | undefined;

  constructor(
    backend: Backend,
    type: string | undefined,
    program: IProgramV5 | undefined,
    channel: ChannelModel | undefined,
    schedule: IScheduleV5 | undefined,
    season: ISeasonV5 | undefined,
    recording: Recording | undefined
  ) {
    super(program?.programId, type, backend);
    this.program = program;
    this.channel = channel;
    this.season = season;
    this.schedule = schedule;
    this.recording = recording;
  }

  override load(): Promise<IDetailDescription> {
    return this._getDetailDescription();
  }

  _getDetailDescription(): Promise<IDetailDescription> {
    return Promise.all([this.getGeneralInfo(), this.getPageSpecificInfo()]).then(([general, specific]) => {
      return {
        ...general,
        ...specific,
      };
    });
  }

  getGeneralInfo(): Promise<GeneralInfo> {
    const result: GeneralInfo = {
      title: '',
      accessibilityIcons: [],
      recordingIcons: [],
      ageRating: undefined,
      synopsis: '',
      credits: undefined,
      bookmarkPercentage: 0,
      blackout: undefined,
    };

    return new Promise(resolve => {
      result.title = this.program?.text.title || '';

      if (this.schedule) {
        result.accessibilityIcons = this._getAccessibilityIcons(this.schedule.accessibilityOptions);
      }
      result.recordingIcons = this.getRecordingIcons();

      const blackoutprom = this.getBlackout();

      //bookmark
      let bookmarkProm;
      if (this.schedule && EpgUtil.scheduleIsLive(this.schedule)) {
        bookmarkProm = Promise.resolve(EpgUtil.calculateProgressPercentage(this.schedule.published));
      } else if (this.program) {
        bookmarkProm = this.getBookmarkPercentage(AssetType.PROGRAM, this.program.programId);
      }

      const ageIcon = getAgeratingIcon(this.program?.minimumAge ? this.program.minimumAge : 0);
      result.ageRating = ageIcon ? { src: ageIcon } : undefined;

      if (this.program) {
        const episodeTitle = lngSupportsAdvancedTextRenderer()
          ? `<b>${this.program.text.episodeTitle}</b>`
          : `${this.program.text.episodeTitle}`;
        const synopsis = this.program.text.longDescription || this.program.text.shortDescription || '';
        result.synopsis = this.program.text.episodeTitle
          ? [episodeTitle, synopsis].join(this.Constants.separator)
          : synopsis;

        result.credits = this.filterCredits(this.program.credits);
      }

      Promise.all([bookmarkProm, blackoutprom])
        .then(([bm, bl]) => {
          result.bookmarkPercentage = bm;
          result.blackout = bl;
          resolve(result);
        })
        .catch(() => {
          resolve(result);
        });
    });
  }

  _getAccessibilityIcons(tags: string[] = []): IconDef[] {
    // comment next line to enable icons without global setting
    if (!this.backend.globalSettings.boolValue(GlobalSettingsKeys.showAccessibilityIcons)) return [];
    // manual if test checking to ensure correct order
    const result = [];
    if (tags.includes(AccessibilityTypes.SUBTITLES)) {
      result.push({
        src: Utils.asset(AccessibilityIcons[AccessibilityTypes.SUBTITLES]),
      });
    }
    if (tags.includes(AccessibilityTypes.AUDIO_DESCRIPTION)) {
      result.push({
        src: Utils.asset(AccessibilityIcons[AccessibilityTypes.AUDIO_DESCRIPTION]),
      });
    }
    if (tags.includes(AccessibilityTypes.SIGN_LANGUAGE)) {
      result.push({
        src: Utils.asset(AccessibilityIcons[AccessibilityTypes.SIGN_LANGUAGE]),
      });
    }
    return result;
  }

  getPageSpecificInfo() {
    if (this.contentType === AssetType.RECORDING) return this.getRecordingSpecificInfo();
    else return this.getProgramSpecificInfo();
  }

  getProgramSpecificInfo() {
    //metainfo
    let metaInformation;
    if (this.schedule) {
      metaInformation = this.getMetaInfo(this.schedule.published.start, this.schedule.published.end);
    }

    //secondary metainfo
    let secondaryMetaInfo;
    if (this.schedule) {
      secondaryMetaInfo = this.getSecondaryMetaInfo(this.schedule.published.start, this.schedule.published.end);
    }

    const play = EpgUtil.getPlayIcon(this.backend, this.schedule, this.recording, this.channel, undefined, false);
    if (play) {
      const playIcon = Utils.asset(play);
      return {
        metaInformation,
        secondaryMetaInfo,
        play: { src: playIcon },
      };
    }

    return {
      metaInformation,
      secondaryMetaInfo,
      play: undefined,
    };
  }

  getRecordingSpecificInfo() {
    let expire = undefined;
    if (this.recording?.isAboutToExpire() && this.recording.expiration) {
      const expireDate = new Date(timeStringToMs(this.recording.expiration));
      expire = getDayInfoString(expireDate, false, this.backend);
      expire = this.backend.translations.forKey(RecordingTranslationKeys.recordings_will_expire).replace('$1', expire);
    }

    let metaInformation = undefined;
    let secondaryMetaInfo = undefined;

    if (this.recording) {
      // primary metainfo
      metaInformation = this.getMetaInfo(this.recording.plannedStart, this.recording.plannedEnd);

      //secondary metainfo
      secondaryMetaInfo = this.getSecondaryMetaInfo(this.recording.plannedStart, this.recording.plannedEnd);
    }

    const play = EpgUtil.getPlayIcon(this.backend, this.schedule, this.recording, this.channel, undefined, false);
    if (play) {
      const playIcon = Utils.asset(play);
      return {
        expire,
        metaInformation,
        secondaryMetaInfo,
        play: { src: playIcon },
      };
    }
    return {
      expire,
      metaInformation,
      secondaryMetaInfo,
      play: undefined,
    };
  }

  getMetaInfo(start: string, end: string) {
    // primary metainfo
    const metaItems = [];
    metaItems.push(this.formatChannelTime(start, end, this.recording));
    metaItems.push(
      this.getFormattedSeasonInfo(this.season ? this.season.seasonNumber : undefined, this.program?.episodeNumber)
    );
    if (this.channel?.text.title) metaItems.push(this.channel.text.title);
    return metaItems.filter(x => x).join(this.Constants.separator);
  }

  getSecondaryMetaInfo(start: string, end: string) {
    let secondaryMetaItems = [];
    if (this.program?.productionYear) secondaryMetaItems.push(this.program.productionYear);
    if (start && end) {
      const msStart = timeStringToMs(start);
      const msEnd = timeStringToMs(end);
      const duration = msEnd - msStart;
      let minutes = duration / 60000;
      minutes = minutes > 1 ? minutes : 1;
      minutes = Math.floor(minutes);
      const minStr = this.backend.translations.forKey(DetailTranslationKeys.detail_duration_min);
      secondaryMetaItems.push(`${minutes} ${minStr}`);
    }
    if (this.program?.genres) {
      secondaryMetaItems = secondaryMetaItems.concat(this.backend.genres.forGenres(this.program.genres));
    }
    return secondaryMetaItems.join(this.Constants.separator);
  }

  getBlackout(): Promise<string | undefined> {
    return Promise.resolve().then(() => {
      return getEpgWatchability(
        this.backend,
        this.schedule,
        this.channel,
        this.recording,
        this.contentType === AssetType.RECORDING
      ).blackoutLabel;
    });
  }

  canWatchLive(): boolean {
    if (!(this.schedule && EpgUtil.scheduleIsLive(this.schedule)) || !this.channel) {
      return false;
    }
    return this.channel.customerHasRight(StreamType.LINEAR);
  }

  canWatchReplay(schedule: IScheduleV5, channel: ChannelModel): boolean {
    if (!EpgUtil.scheduleIsReplay(schedule, channel) || !channel) {
      return false;
    }

    return channel.customerHasRight(StreamType.REPLAY);
  }

  getRecordingIcons(): { src: string }[] {
    return EpgUtil.getRecordingIcons(this.recording);
  }

  formatChannelTime(
    plannedStart: string | undefined,
    plannedEnd: string | undefined,
    recording: Recording | undefined
  ): string {
    if (!(plannedStart && plannedEnd) && !recording) {
      return '';
    }

    let start;
    let end;
    if (plannedStart && plannedEnd) {
      start = plannedStart;
      end = plannedEnd;
    } else if (recording?.start && recording.end) {
      start = recording.start;
      end = recording.end;
    }

    if (!start || !end) {
      return '';
    }

    let scheduleInfo = getDayInfoString(new Date(timeStringToMs(start)), false, this.backend);

    scheduleInfo += `, ${getTime(new Date(timeStringToMs(start)))}`;
    scheduleInfo += ` - ${getTime(new Date(timeStringToMs(end)))}`;
    return scheduleInfo;
  }
}
